class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* ------------------------------------------------------------------
   Upsell-carrousel in de winkelwagen (<geo-upsell>)
   1. Vaste producten + metafield custom.upsell_products (uit snippets/geo-upsell.liquid).
   2. Aangevuld met Shopify's automatische aanbevelingen tot 5 producten.
   Producten die al in de winkelwagen zitten worden overgeslagen.
------------------------------------------------------------------- */
if (!customElements.get('geo-upsell')) {
  class GeoUpsell extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded) return;
      this.dataset.loaded = '1';
      this.load();
    }

    get data() {
      try {
        return JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      } catch (e) {
        return null;
      }
    }

    async load() {
      const d = this.data;
      if (!d) {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.load(), { once: true });
        }
        return;
      }
      const max = d.max || 5;
      const inCart = new Set((d.cartProductIds || []).map(String));
      const seen = new Set();
      const list = [];
      const push = (p) => {
        const id = String(p.id);
        if (list.length >= max || inCart.has(id) || seen.has(id)) return;
        seen.add(id);
        list.push(p);
      };
      (d.products || []).forEach(push);

      const anchors = d.anchorIds || [];
      for (const anchor of anchors) {
        if (list.length >= max) break;
        try {
          const res = await fetch(`${d.recUrl}.json?product_id=${anchor}&limit=10&intent=related`);
          const json = await res.json();
          (json.products || []).forEach((p) => {
            if (!p.available) return;
            const v = (p.variants || []).find((x) => x.available) || p.variants[0];
            if (!v || v.price <= 0) return;
            push({
              id: p.id,
              title: p.title,
              url: p.url,
              image: p.featured_image,
              price: v.price,
              compare: v.compare_at_price || 0,
              variantId: v.id,
            });
          });
        } catch (e) {}
      }

      if (!list.length) return;
      this.render(list, d);
    }

    money(cents, d) {
      try {
        return new Intl.NumberFormat(d.locale || 'nl', { style: 'currency', currency: d.currency || 'EUR' }).format(
          cents / 100
        );
      } catch (e) {
        return (cents / 100).toFixed(2);
      }
    }

    img(src) {
      if (!src) return '';
      const s = src.startsWith('//') ? 'https:' + src : src;
      return s.includes('?') ? s + '&width=200' : s + '?width=200';
    }

    esc(t) {
      const el = document.createElement('div');
      el.textContent = t || '';
      return el.innerHTML;
    }

    render(list, d) {
      const cards = list
        .map(
          (p) => `
        <li class="geo-upsell__card">
          <a href="${p.url}" class="geo-upsell__media" tabindex="-1" aria-hidden="true">
            ${p.image ? `<img src="${this.img(p.image)}" alt="" loading="lazy" width="100" height="100">` : ''}
          </a>
          <div class="geo-upsell__info">
            <a href="${p.url}" class="geo-upsell__title">${this.esc(p.title)}</a>
            <span class="geo-upsell__price">${this.money(p.price, d)}${
            p.compare > p.price ? `<s class="geo-upsell__compare">${this.money(p.compare, d)}</s>` : ''
          }</span>
          </div>
          <button type="button" class="geo-upsell__add" data-variant="${p.variantId}" aria-label="${this.esc(
            d.addLabel
          )}: ${this.esc(p.title)}">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="20" r="1.3"/><circle cx="18" cy="20" r="1.3"/><path d="M2 3h3l2.7 12.4a1.5 1.5 0 0 0 1.5 1.1h8.6a1.5 1.5 0 0 0 1.5-1.1L21 7H6.2"/></svg>
          </button>
        </li>`
        )
        .join('');

      this.innerHTML = `
        <p class="geo-upsell__heading">${this.esc(d.heading)}</p>
        <div class="geo-upsell__wrap">
          <button type="button" class="geo-upsell__nav geo-upsell__nav--prev" aria-label="‹">‹</button>
          <ul class="geo-upsell__track" role="list">${cards}</ul>
          <button type="button" class="geo-upsell__nav geo-upsell__nav--next" aria-label="›">›</button>
        </div>
        <div class="geo-upsell__dots">${list.map((_, i) => `<span class="${i === 0 ? 'is-active' : ''}"></span>`).join('')}</div>`;
      this.hidden = false;

      const track = this.querySelector('.geo-upsell__track');
      const dots = [...this.querySelectorAll('.geo-upsell__dots span')];
      const step = () => track.clientWidth;
      this.querySelector('.geo-upsell__nav--prev').addEventListener('click', () =>
        track.scrollBy({ left: -step(), behavior: 'smooth' })
      );
      this.querySelector('.geo-upsell__nav--next').addEventListener('click', () =>
        track.scrollBy({ left: step(), behavior: 'smooth' })
      );
      track.addEventListener(
        'scroll',
        () => {
          const i = Math.round(track.scrollLeft / Math.max(step(), 1));
          dots.forEach((dot, n) => dot.classList.toggle('is-active', n === i));
        },
        { passive: true }
      );
      this.querySelectorAll('.geo-upsell__add').forEach((btn) =>
        btn.addEventListener('click', () => this.add(btn))
      );
    }

    async add(btn) {
      if (btn.disabled) return;
      btn.disabled = true;
      btn.classList.add('is-loading');
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer && drawer.getSectionsToRender ? drawer.getSectionsToRender().map((s) => s.id) : [];
      const addUrl = ((window.routes && window.routes.cart_add_url) || '/cart/add') + '.js';
      try {
        const res = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(btn.dataset.variant), quantity: 1 }],
            sections: sections,
            sections_url: window.location.pathname,
          }),
        });
        const json = await res.json();
        if (json.status) throw new Error(json.description || 'error');
        if (drawer && json.sections) {
          drawer.renderContents(json);
        } else {
          window.location.reload();
        }
      } catch (e) {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }
    }
  }
  customElements.define('geo-upsell', GeoUpsell);
}

/* ------------------------------------------------------------------
   Snelle afrekenknoppen in de winkelwagen (.geo-express)
   Shop Pay eruit halen, inclusief de lege plek die het anders inneemt.
   Welke andere knoppen (Apple Pay, Google Pay, PayPal) verschijnen, bepaalt Shopify
   zelf per apparaat; daar komen we niet aan.
------------------------------------------------------------------- */
(function () {
  const TAG = 'shopify-accelerated-checkout-cart';

  function fix(el) {
    if (!el || el.hasAttribute('data-geo-fixed')) return;
    let cfg;
    try {
      cfg = JSON.parse(el.getAttribute('wallet-configs') || '[]');
    } catch (e) {
      return;
    }
    if (!cfg.some((c) => c.name === 'shop_pay')) {
      el.setAttribute('data-geo-fixed', '1');
      return;
    }
    const keep = cfg.filter((c) => c.name !== 'shop_pay');
    if (!keep.length) {
      const box = el.closest('.geo-express');
      if (box) box.style.display = 'none';
      return;
    }
    const t = document.createElement('template');
    t.innerHTML = el.outerHTML;
    const fresh = t.content.firstElementChild;
    fresh.setAttribute('wallet-configs', JSON.stringify(keep));
    fresh.setAttribute('data-geo-fixed', '1');
    fresh.querySelectorAll('shop-pay-wallet-button').forEach((c) => c.remove());
    el.replaceWith(fresh);
  }

  function scan() {
    document.querySelectorAll(`#CartDrawer .geo-express ${TAG}:not([data-geo-fixed])`).forEach(fix);
  }

  function start() {
    scan();
    const root = document.getElementById('CartDrawer');
    if (root) new MutationObserver(scan).observe(root, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
